import { ActionList, ActionMenu } from '@primer/react';
import { KBUrl } from '@upright/ui/common/upright/kb-urls';
import {
  BookOpen,
  FileDocumentInfoQuickReferenceStreamlineCore,
  MailSendEnvelopeStreamlineCore,
  QuestionCircle,
} from '@upright/ui/icons';
import { useMainContext } from '@upright/ui/views/root/main/context';
import { useState } from 'react';
import { DataDisclaimerDialog } from './data-disclaimer-dialog';
import styles from './unified-top-bar.module.css';

/**
 * The Help menu in the unified shell's top bar. Its entries are the dark header's user-menu
 * items that were about getting help rather than about the account — the knowledge base and
 * Contact — so an investor finds them under the word the corporate bar already uses for the
 * same slot.
 *
 * The knowledge base row is named *Upright Knowledge Base*, which is what the site calls
 * itself; a bare "Knowledge base" reads as a generic section of this product rather than as
 * the destination the link actually opens.
 *
 * *Data sources and disclaimer* opens the model release, the fund holdings data source and the
 * legal terms that the legacy views print under every lens (PLT-6866). The unified profiles
 * drop that footer, so this row is where those statements live for the investor side.
 *
 * **About Upright Platform is not one of them.** That row opened `AboutUprightModal`, which
 * renders the *mobile footer* verbatim rather than a designed surface (its own file says so),
 * and it carried the product's legal links as its only reason to exist. The links moved to the
 * account menu's footer (`unified-account-menu.tsx`); the modal stays in the repo because the
 * dark header still opens it.
 *
 * Three rows, ungrouped, in a `small` overlay. The corporate bar's Help menu splits its eight
 * rows under *Tutorials* and *Resources* headings, in a wider panel; this menu takes neither.
 * A heading over a single row names a group of one, which reads as a defect rather than as
 * structure. `small` (256px) still clears the longest label.
 *
 * Contact Upright and the top bar's Feedback control open the shared feedback form.
 * Keeping the Help entry makes the contact channel discoverable for support requests too.
 */
export function UnifiedHelpMenu() {
  const [, setContactModalOpen] = useMainContext().contactModal;
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          <button type="button" className={styles.headerItem} aria-label="Help">
            <span className={styles.headerItemIcon}>
              <QuestionCircle width={20} height={20} aria-hidden="true" />
            </span>
            <span className={styles.headerItemLabel}>Help</span>
          </button>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small" align="end">
          <ActionList className={styles.helpMenu}>
            <ActionList.LinkItem href={KBUrl.INDEX} target="_blank" rel="noopener noreferrer">
              <ActionList.LeadingVisual>
                <BookOpen width={20} height={20} />
              </ActionList.LeadingVisual>
              Upright Knowledge Base
            </ActionList.LinkItem>
            <ActionList.Item
              onSelect={() => {
                setContactModalOpen(true);
              }}
            >
              <ActionList.LeadingVisual>
                <MailSendEnvelopeStreamlineCore width={20} height={20} />
              </ActionList.LeadingVisual>
              Contact Upright
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => {
                setDisclaimerOpen(true);
              }}
            >
              <ActionList.LeadingVisual>
                <FileDocumentInfoQuickReferenceStreamlineCore width={20} height={20} />
              </ActionList.LeadingVisual>
              Data sources and disclaimer
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <DataDisclaimerDialog
        isOpen={disclaimerOpen}
        onClose={() => {
          setDisclaimerOpen(false);
        }}
      />
    </>
  );
}
